import { css } from '@inter-ikea-kompis/web-component';
import type { ITheme } from '@inter-ikea-kompis/types';

import { StyleUtility } from '@inter-ikea-kompis/component-content-margins';

import { Text } from '@ingka/common-styles-webc';

import SummaryPagePaddingUtility from '../../utilities/SummaryPagePaddingUtility.js';

/**
 * This is the style class for SummaryPage.
 */
export default class SummaryPageStyle {
	/**
	 * Returns styles.
	 *
	 * @param options ...
	 * @param options.theme ...
	 * @param options.isOldSafari ...
	 * @param options.enableMargins ...
	 * @param options.enableMainMargins ...
	 * @param options.direction ...
	 * @param options.includesScreenshot ...
	 */
	public static getStyle(options: {
		theme: ITheme;
		isOldSafari: boolean;
		enableMargins: boolean;
		enableMainMargins: boolean;
		direction: string | null;
		includesScreenshot: boolean;
	}): string {
		const { breakpoint, spacing, color } = options.theme;

		const margins = {
			horizontal: {
				small: spacing.space150,
				medium: spacing.space200,
				large: spacing.space250,
				xlarge: spacing.space300,
				xxlarge: spacing.space300
			},
			vertical: {
				small: spacing.space150,
				medium: spacing.space125,
				large: spacing.space200,
				xlarge: spacing.space250,
				xxlarge: spacing.space250
			}
		};

		const gutters = {
			small: spacing.space75,
			medium: spacing.space200,
			large: spacing.space150,
			xlarge: spacing.space75,
			xxlarge: spacing.space75
		};

		const { theme, direction, includesScreenshot } = options;

		/* XXL in Kompis SkapaTheme is 1920, while Skapas is 1600, so we hardcode it here */
		const xxLargeBreakPoint = 1600;

		const oldSafariStyles = css`
			.grid {
				display: flex;
				flex-direction: column-reverse;
			}
		`;

		const marginStyles = css`
			.container {
				padding-top: ${margins.vertical.small / 16}rem;
				padding-bottom: ${margins.vertical.small / 16}rem;
			}
			@media (min-width: ${breakpoint.medium.width / 16}rem) {
				.container {
					padding-top: ${margins.vertical.medium / 16}rem;
					padding-bottom: ${margins.vertical.medium / 16}rem;
				}
			}
			@media (min-width: ${breakpoint.large.width / 16}rem) {
				.container {
					padding-top: ${margins.vertical.large / 16}rem;
					padding-bottom: ${margins.vertical.large / 16}rem;
				}
			}
			@media (min-width: ${breakpoint.xlarge.width / 16}rem) {
				.container {
					padding-top: ${margins.vertical.xlarge / 16}rem;
					padding-bottom: ${margins.vertical.xlarge / 16}rem;
				}
			}

			${SummaryPagePaddingUtility.getHorizontalPadding('.container', options.theme)}
		`;

		const layoutMargins = css`
			${StyleUtility.getSpaceForHeader({ cssSelector: '.wrapper', theme })}
			${StyleUtility.getLayoutParentStyling({ cssSelector: '.wrapper', theme })}
			${StyleUtility.getHorizontalSpacing({ cssSelector: '.wrapper', theme, direction })};
			${StyleUtility.getLayoutChildStyling({ cssSelector: '.container', theme })}
		`;

		return css`
			${Text.CSS}

			${options.isOldSafari ? oldSafariStyles : ''}
			${options.enableMargins ? marginStyles : ''}
			${options.enableMainMargins ? layoutMargins : ''}

			.visuallyHidden {
				border: 0 !important;
				clip: rect(${1 / 16}rem, ${1 / 16}rem, ${1 / 16}rem, ${1 / 16}rem) !important;
				-webkit-clip-path: inset(50%) !important;
				clip-path: inset(50%) !important;
				height: ${1 / 16}rem !important;
				margin: -${1 / 16}rem !important;
				overflow: hidden !important;
				padding: 0 !important;
				position: absolute !important;
				width: ${1 / 16}rem !important;
				white-space: nowrap !important;
				top: 0;
				bottom: 0;
				left: 0;
				right: 0;
				-webkit-user-select: none;
				user-select: none;
			}

			.title {
				display: block;
				color: ${color.text.dark};
				margin: 0;
			}

			.heading-area {
				margin-bottom: ${spacing.space250 / 16}rem;
				display: flex;
				flex-wrap: wrap;
				gap: ${spacing.space50 / 16}rem;
				align-items: center;
				width: 100%;
			}

			.heading-area :first-child {
				flex-grow: 1;
			}

			.details {
				max-width: 100%;
			}

			.overview {
				display: grid;
				grid-template-columns: repeat(4, 1fr);
				column-gap: ${gutters.small / 16}rem;
			}

			.screenshot,
			.overview-sticky {
				grid-column: 1 / span 4;
			}

			.screenshot {
				width: 100%;
				margin-bottom: ${spacing.space200 / 16}rem;
			}

			kompis-configuration-summary,
			kompis-assistance-card,
			kompis-assembly-services,
			skapa-inline-message:last-of-type {
				margin-bottom: ${spacing.space300 / 16}rem;
			}

			.measurements {
				color: ${color.text.default};
			}

			.qr-code {
				display: none;
			}

			.no-margin {
				margin: 0;
			}

			@media (min-width: ${breakpoint.medium.width / 16}rem) {
				.overview {
					display: ${includesScreenshot ? 'grid' : 'block'};
					grid-template-columns: repeat(6, 1fr);
					column-gap: ${gutters.medium / 16}rem;
				}

				.overview-sticky {
					grid-column: 4 / span 3;
				}

				.screenshot {
					grid-column: 1 / span 3;
				}
			}

			@media (min-width: ${breakpoint.large.width / 16}rem) {
				.grid {
					display: grid;
					grid-auto-flow: column dense;
					grid-template-columns: repeat(12, 1fr);
					column-gap: ${gutters.large / 16}rem;
				}

				.details,
				.heading-area {
					grid-column: 1 / span 7;
				}

				.details,
				.overview {
					grid-row: 2;
				}

				.overview {
					display: grid;
					grid-column: 9 / span 4;
					grid-template-columns: repeat(4, 1fr);
					column-gap: ${gutters.large / 16}rem;
					align-content: start;
				}

				.overview-sticky {
					position: sticky;
					display: grid;
					top: ${spacing.space250 / 16}rem;
					grid-template-columns: repeat(4, 1fr);
				}

				kompis-configuration-summary {
					max-width: unset;
					grid-row: unset;
				}

				kompis-configuration-summary,
				.screenshot,
				kompis-assistance-card,
				kompis-assembly-services,
				skapa-inline-message,
				.overview-sticky,
				.qr-code {
					grid-column: 1 / span 4;
				}

				.qr-code {
					display: flex;
					flex-direction: column;
					padding: ${spacing.space125 / 16}rem;
					background-color: ${color.neutral.grey100};
				}

				kompis-qr-code {
					width: ${132 / 16}rem;
					margin: 0 auto;
					background-color: ${color.neutral.white};
				}

				.qr-code-text {
					margin-top: ${spacing.space125 / 16}rem;
					text-align: center;
					color: ${color.text.dark};
				}
			}

			@media (min-width: ${breakpoint.xlarge.width / 16}rem) {
				.grid,
				.overview {
					column-gap: ${gutters.xlarge / 16}rem;
				}

				.heading-area {
					gap: ${spacing.space75 / 16}rem;
				}
			}

			@media (min-width: ${xxLargeBreakPoint / 16}rem) {
				.container {
					margin: auto;
					max-width: ${xxLargeBreakPoint / 16}rem;
				}
			}

			skapa-inline-message {
				margin-bottom: ${spacing.space200 / 16}rem;
			}

			skapa-inline-message h3 {
				margin: 0;
			}

			@media print {
				.wrapper {
					padding-left: 0;
					padding-right: 0;
					margin-top: 0;
				}

				.container {
					padding: 0 0.25in;
					/* Will only have effect when .wrapper have display: grid (set always to reduce complexity). */
					grid-column-start: 1;
					grid-column-end: -1;
				}

				.grid {
					/* Chrome doesn't support "break-inside: avoid" in product list if parent container is flex display. Using table to reorder child container instead. */
					display: table;
					/* Safari doesn't resize when printing, so we do it here. */
					margin: auto;
				}

				.heading-area,
				.overview {
					display: none;
				}

				.details {
					display: table-footer-group;
				}

				.print-header {
					display: flex;
				}

				.print-header .title {
					color: ${color.text.dark};
				}

				.hidden-print {
					display: none;
				}
			}
		`;
	}
}
